import { Notification } from '@/types/notifications'

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * A notification's age as the popover shows it: "Just now", "5m ago",
 * "3h ago", "Yesterday", a weekday within the last week, then a date.
 */
export function formatNotificationTime(value: string) {
  const now = new Date()
  const date = new Date(value)
  const diffMs = now.getTime() - date.getTime()

  if (diffMs < MINUTE_MS) return 'Just now'
  if (diffMs < HOUR_MS) return `${Math.floor(diffMs / MINUTE_MS)}m ago`

  const daysAgo = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) / (24 * HOUR_MS),
  )
  if (daysAgo === 0) return `${Math.floor(diffMs / HOUR_MS)}h ago`
  if (daysAgo === 1) return 'Yesterday'
  if (daysAgo < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'long' })
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  })
}

/**
 * The web route that opens a notification's destination. Browser push clicks
 * and notification emails build this same path, so keep it stable. The push
 * service worker can't import this, so openPath() in
 * public/firebase-messaging-sw.js builds it separately and must match.
 * A notification with no link opens nothing, so this returns null for it.
 */
export function getNotificationOpenPath({
  link_kind,
  link_obj_id,
}: Pick<Notification, 'link_kind' | 'link_obj_id'>) {
  if (!link_kind || !link_obj_id) return null
  return `/open/${link_kind}/${link_obj_id}`
}
